import React, { useState, memo } from 'react';
import { ChevronDown } from 'lucide-react';
import { FadeIn } from './ui/FadeIn';
import { Button } from './ui/Button';
interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}
function FAQItem({
  question,
  answer,
  isOpen,
  onClick
}: FAQItemProps) {
  return <div className={`border border-border-default rounded-sm overflow-hidden transition-colors duration-300 ${isOpen ? 'bg-elevated border-border-accent' : 'bg-surface hover:border-border-muted'}`} data-id="element-221">
      <button className="w-full px-6 py-5 flex items-center justify-between text-left" onClick={onClick} data-id="element-222">
        <span className={`font-ui font-medium text-lg ${isOpen ? 'text-accent-primary' : 'text-text-primary'}`} data-id="element-223">
          {question}
        </span>
        <ChevronDown className={`w-5 h-5 text-text-muted transition-transform duration-300 ${isOpen ? 'rotate-180 text-accent-primary' : ''}`} data-id="element-224" />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-225">
        <div className="px-6 pb-6 font-body text-lg text-text-secondary leading-relaxed border-t border-border-default/50 pt-4" data-id="element-226">
          {answer}
        </div>
      </div>
    </div>;
}
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    question: 'Is this a political book?',
    answer: "No. It's an editorial memorial. It documents facts, historical context, and cultural consequences — it doesn't take sides with any political party."
  }, {
    question: "Can I read it if I'm not conservative?",
    answer: 'Absolutely. This book addresses free speech, academia, media, and democracy — topics that transcend ideology.'
  }, {
    question: 'How do I know the information is credible?',
    answer: "Each chapter is supported by references to real studies (FIRE, Pew Research, Media Research Center, Gallup). It's not opinion dressed as fact."
  }, {
    question: 'Is this only for Americans?',
    answer: 'No. The book has dedicated chapters on the echo in Europe, Latin America, and other Western democracies.'
  }, {
    question: 'Why buy digital?',
    answer: 'Instant access, no shipping, compatible with any device. You have it in minutes, not weeks.'
  }, {
    question: "What if I don't like it?",
    answer: '7-day guarantee. Full refund. No questions.'
  }, {
    question: 'Is this just a fan tribute?',
    answer: "No. This is an editorial document. It covers Kirk's legacy critically and contextually — including the debates around his influence, not just praise."
  }];
  return <section className="py-20 md:py-28 px-6 bg-base" data-id="element-227">
      <div className="max-w-3xl mx-auto" data-id="element-228">
        {/* Section label */}
        <FadeIn data-id="element-229">
          <div className="flex items-center gap-4 mb-16" data-id="element-230">
            <div className="h-px bg-border-accent flex-grow" data-id="element-231" />
            <span className="font-ui text-xs font-semibold tracking-widest uppercase text-accent-primary" data-id="element-232">
              ◆ Questions ◆
            </span>
            <div className="h-px bg-border-accent flex-grow" data-id="element-233" />
          </div>
        </FadeIn>

        <FadeIn delay={0.1} data-id="element-234">
          <h2 className="font-display text-3xl md:text-5xl font-bold text-text-primary mb-14 text-center leading-tight" data-id="element-235">
            Frequently Asked Questions
          </h2>
        </FadeIn>

        <div className="space-y-4" data-id="element-236">
          {faqs.map((faq, index) => <FadeIn key={index} delay={0.15 + index * 0.05} data-id="element-237">
              <FAQItem question={faq.question} answer={faq.answer} isOpen={openIndex === index} onClick={() => setOpenIndex(openIndex === index ? null : index)} data-id="element-238" />
            </FadeIn>)}
        </div>

        {/* CTA after objections are handled */}
        <FadeIn delay={0.6} data-id="element-239">
          <div className="flex flex-col items-center mt-14 text-center" data-id="element-240">
            <p className="font-display text-xl italic text-text-secondary mb-6" data-id="element-241">
              Still have questions? The book has 170 pages of answers.
            </p>
            <Button href="#offer" className="w-full md:w-auto text-sm md:text-base" data-id="element-242">
              Get The Memorial Edition &rarr;
            </Button>
            <p className="font-ui text-xs text-text-muted mt-4 tracking-wide" data-id="element-243">
              7-day money-back guarantee. Zero risk.
            </p>
          </div>
        </FadeIn>
      </div>
    </section>;
}